import { useCallback, useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import config from 'constants/config'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DAY_ONLY } from 'constants/dateFormats'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import OrderCarrierLogoHero from 'components/Common/OrderCarrierLogoHero'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import useOffer from 'hooks/Offers/useOffer'
import useExperience from 'hooks/Experiences/useExperience'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'
import useCruisePurchaseDetails from 'hooks/Cruise/useCruisePurchaseDetails'
import useFlightPurchaseDetails from 'hooks/Flights/useFlightPurchaseDetails'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getDaysToGoLabel } from 'checkout/Components/Confirmation/BookingDetailsV2/utils'
import UpcomingEscapeUpsellList from './UpcomingEscapeUpsellList'
import type { UpcomingEscapeUpsellsResult } from './useUpcomingEscapeUpsells'
import getUpcomingEscapeBookingTitle from './getUpcomingEscapeBookingTitle'
import getUpcomingEscapePrimaryBooking from './getUpcomingEscapePrimaryBooking'

const Section = styled(LayoutContainer)`
  padding-top: ${rem(40)};
`

const Card = styled(Sheet)`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: stretch;
    gap: ${rem(24)};
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: ${rem(160)};
  flex-shrink: 0;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    width: ${rem(220)};
    min-height: ${rem(160)};
    height: auto;
    align-self: stretch;
    border-radius: ${props => props.theme.borderRadius.L};
  }
`

const ExternalHeroImage = styled(Image)`
  width: 100%;
  height: 100%;
`

const Content = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

interface Props {
  order: App.Order
  upsells: UpcomingEscapeUpsellsResult
}

function UpcomingEscapeFlightHero({ order }: { order: App.Order }) {
  const { carrierBrandMarks, carrierLogos } = useFlightPurchaseDetails(order)
  const logos = useMemo(() => {
    const carrierImages = carrierBrandMarks.length > 0 ? carrierBrandMarks : carrierLogos
    return carrierImages.filter((logo): logo is string => !!logo)
  }, [carrierBrandMarks, carrierLogos])

  return <OrderCarrierLogoHero logos={logos} data-testid="hero-flight-logos" />
}

function UpcomingEscapeCard({ order, upsells }: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const primaryBooking = useMemo(() => getUpcomingEscapePrimaryBooking(order), [order])
  const stayOfferId = primaryBooking.kind === 'stay' ? primaryBooking.stayOfferId : undefined
  const experienceId = primaryBooking.kind === 'experience' ? primaryBooking.experienceId : undefined
  const tourReservationId = primaryBooking.kind === 'tour' ? primaryBooking.tourReservationId : undefined
  const tourOrderId = primaryBooking.kind === 'tour' ? order.id : undefined
  const cruiseBookingId = primaryBooking.kind === 'cruise' ? primaryBooking.cruiseBookingId : undefined
  const [offer] = useOffer<App.Offer | App.BedbankOffer>(stayOfferId)
  const [experience] = useExperience(experienceId)
  const [tourSnapshot] = useTourV2Snapshot(tourReservationId, tourOrderId)
  const { image: cruiseImageId } = useCruisePurchaseDetails(order, cruiseBookingId)
  const checkInDate = primaryBooking.startDate
  const checkOutDate = primaryBooking.endDate

  const formatFlightRoute = useCallback((departureAirport: string, arrivalAirport: string) => (
    intl.formatMessage(
      {
        id: '1ywXJcItUQtqDs9eHBqt',
        defaultMessage: '{departureAirport} to {arrivalAirport}',
        description: 'Standalone flight route title on the homepage Upcoming Escape card when only airport codes are available',
      },
      { departureAirport, arrivalAirport },
    )
  ), [intl])

  const bookingTitle = getUpcomingEscapeBookingTitle(order, offer, formatFlightRoute)
  const experienceImage = primaryBooking.kind === 'experience' ? experience?.images?.[0] : undefined
  const imageId = (primaryBooking.kind === 'stay' ? offer?.image?.id : undefined)
    || experienceImage?.id
    || (primaryBooking.kind === 'tour' ? tourSnapshot?.imageId : undefined)
    || (primaryBooking.kind === 'cruise' ? cruiseImageId : undefined)
    || primaryBooking.imageId

  const dateLabel = useMemo(() => {
    if (!checkInDate || !checkOutDate) return ''
    const startDate = dateFormatter(checkInDate, INTL_DAY_ONLY)
    const endDate = dateFormatter(checkOutDate, INTL_DAY_MONTH_NAME_SHORT)
    if (!startDate || !endDate) return ''

    return intl.formatMessage(
      {
        id: 'R3Xba2_yeMYjJ9kQ1fba',
        defaultMessage: '{startDate} - {endDate}',
        description: 'Short stay date range on the homepage Upcoming Escape card',
      },
      { startDate, endDate },
    )
  }, [checkInDate, checkOutDate, dateFormatter, intl])

  const daysToGoLabel = getDaysToGoLabel(
    intl,
    primaryBooking.startDate,
    primaryBooking.duration,
    primaryBooking.kind,
  )

  const manageBookingLink = useCallback((chunks: ReactNode) => (
    <TextLink
      key="manage-booking"
      to={config.agentHub.isEnabled ? undefined : `/account/my-escapes/order/${order.id}`}
      href={config.agentHub.isEnabled ? `${config.AGENT_PORTAL_URL}/bookings` : undefined}
      target={config.agentHub.isEnabled ? '_blank' : undefined}
      weight="regular"
      data-testid="manage-booking"
    >
      {chunks}
    </TextLink>
  ), [order.id])

  const bookingMeta = dateLabel
    ? intl.formatMessage(
        {
          id: 'cRAD2BmT4RNK7UAN7byo',
          defaultMessage: '{dateLabel} · <link>Manage booking</link>',
          description: 'Date label with manage booking link on the homepage Upcoming Escape card',
        },
        {
          dateLabel,
          link: manageBookingLink,
        },
      )
    : intl.formatMessage(
        {
          id: 'osNsihazW8PZ1ELQ2Hh0',
          defaultMessage: '<link>Manage booking</link>',
          description: 'Manage booking link on Upcoming Escape when stay dates are unavailable',
        },
        {
          link: manageBookingLink,
        },
      )

  return (
    <Section data-testid="upcoming-escape-section">
      <Card border="light-foreground" borderRadius="L" padding={0} tabletPadding={24} shadow="flat-small">
        <ImageContainer data-testid="hero-slot">
          {imageId && (
            <HeightFillingImage fit="center" id={imageId} />
          )}
          {!imageId && experienceImage && (
            <ExternalHeroImage image={experienceImage} alt="" fit="center" objectFit="cover" />
          )}
          {!imageId && !experienceImage && primaryBooking.kind === 'flight' && (
            <UpcomingEscapeFlightHero order={order} />
          )}
        </ImageContainer>
        <Content>
          <Group direction="vertical" gap={8}>
            {daysToGoLabel && (
              <LabelGroup>
                <UrgencyLabel variant="speed">{daysToGoLabel}</UrgencyLabel>
              </LabelGroup>
            )}
            {bookingTitle && <Heading variant="heading3">{bookingTitle}</Heading>}
            <BodyText variant="medium">{bookingMeta}</BodyText>
          </Group>
          <UpcomingEscapeUpsellList order={order} upsells={upsells} />
        </Content>
      </Card>
    </Section>
  )
}

export default UpcomingEscapeCard
